import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Swal from "sweetalert2";
import SessionsApi from "../../../Classes/API/Administration/SessionsApi";

const SessionSelectionPopup = ({ open, onClose, onSessionsSelected }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [sessions, setSessions] = useState([]); // All sessions from API
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchAvailableSessions();
    }
  }, [open]);

  const fetchAvailableSessions = async () => {
    try {
      setIsLoading(true);
      setSessions([]);
      setSelectedSessions([]);

      const request = {
        noContract: true,
        pagingParams: {
          pageNumber: 1,
          pageSize: 1000,
        },
      };

      const response = await SessionsApi.SearchSessions(request);
      if (response.status === "Success") {
        setSessions(response.data.list);
        console.log("Fetched sessions:", response.data.list);
      } else {
        Swal.fire({
          title: "Error",
          text: response.message || "Failed to fetch sessions",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch accounting sessions",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedSessions([]);
      setSearchTerm("");
    }
  }, [open]);

  const handleSessionSelect = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      setSelectedSessions(selectedSessions.filter((id) => id !== sessionId));
    } else {
      setSelectedSessions([...selectedSessions, sessionId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedSessions.length === filteredSessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(filteredSessions.map((session) => session.id));
    }
  };

  const handleConfirm = () => {
    if (selectedSessions.length === 0) {
      Swal.fire({
        title: "Warning",
        text: "Please select at least one session",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    onSessionsSelected(selectedSessions);
    onClose();
  };

  // Handle cancel/close
  const handleCancel = () => {
    onClose();
  };

  // Format price based on currency type
  const formatPrice = (session) => {
    const prices = [];
    if (session.egyptianPrice > 0) {
      prices.push(`EGP: ${session.egyptianPrice}`);
    }
    if (session.arabPrice > 0) {
      prices.push(`Arab: ${session.arabPrice}`);
    }
    if (session.otherPrice > 0) {
      prices.push(`Other: ${session.otherPrice}`);
    }
    return prices.join(" | ");
  };

  const filteredSessions =
    searchTerm.trim() === ""
      ? sessions
      : sessions.filter(
          (session) =>
            session.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            session.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            session.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="lg" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6">Select Sessions for Contract</MDTypography>
          <IconButton onClick={handleCancel} size="small">
            <Close />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent>
        {/* Search Bar */}
        <MDBox mb={2} mt={1}>
          <MDInput
            fullWidth
            placeholder="Search sessions by name, category, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: <Search sx={{ color: "text.secondary" }} />,
            }}
          />
        </MDBox>

        {/* Selection Info */}
        <MDBox mb={1} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="body2" color="textSecondary">
            {filteredSessions.length} available session(s)
          </MDTypography>
          {selectedSessions.length > 0 && (
            <MDTypography variant="body2" color="info">
              {selectedSessions.length} session(s) selected
            </MDTypography>
          )}
        </MDBox>

        {/* Select All Checkbox */}
        {filteredSessions.length > 0 && (
          <MDBox mb={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    filteredSessions.length > 0 &&
                    selectedSessions.length === filteredSessions.length
                  }
                  indeterminate={
                    selectedSessions.length > 0 && selectedSessions.length < filteredSessions.length
                  }
                  onChange={handleSelectAll}
                />
              }
              label="Select All Available Sessions"
            />
          </MDBox>
        )}

        {/* Sessions List */}
        <MDBox sx={{ maxHeight: "400px", overflow: "auto" }}>
          {isLoading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={24} />
              <MDTypography variant="body2" sx={{ ml: 2 }}>
                Loading sessions...
              </MDTypography>
            </MDBox>
          ) : filteredSessions.length > 0 ? (
            filteredSessions.map((session) => (
              <MDBox
                key={session.id}
                display="flex"
                alignItems="flex-start"
                mb={2}
                p={1}
                onClick={() => handleSessionSelect(session.id)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <Checkbox
                  checked={selectedSessions.includes(session.id)}
                  onChange={() => handleSessionSelect(session.id)}
                  sx={{ mt: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                />
                <MDBox ml={1} flexGrow={1}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                    <MDTypography variant="body1" fontWeight="bold">
                      {session.name}
                    </MDTypography>
                    <MDTypography variant="body2" color="textSecondary">
                      {session.category}
                    </MDTypography>
                  </MDBox>

                  {session.description && (
                    <MDTypography variant="body2" sx={{ mt: 0.5, mb: 1 }}>
                      {session.description}
                    </MDTypography>
                  )}

                  <MDBox display="flex" justifyContent="space-between" alignItems="center">
                    <MDTypography variant="caption" color="textSecondary">
                      {formatPrice(session)}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      Added: {new Date(session.timeStamp).toLocaleDateString()}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            ))
          ) : searchTerm ? (
            <MDTypography variant="body2" color="textSecondary" textAlign="center" py={3}>
              No available sessions found matching "{searchTerm}"
            </MDTypography>
          ) : (
            <MDTypography variant="body2" color="textSecondary" textAlign="center" py={3}>
              No available sessions without contracts found
            </MDTypography>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleCancel} color="secondary">
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          variant="gradient"
          color="info"
          disabled={selectedSessions.length === 0}
        >
          Confirm Selection ({selectedSessions.length})
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default SessionSelectionPopup;
